import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  type AccessSettings,
  createUserSchema,
  expectedSignInMethod,
  NO_ACCESS,
  type ProfileResponse,
  updateUserSchema,
  type UserResponse,
} from '@k-management/shared';
import { type FormEvent, useEffect, useRef, useState } from 'react';
import { useAuth, useCurrentUser } from '../auth/AuthProvider.js';
import { apiRequest } from '../lib/api.js';
import { type FieldErrors, showServerError, validate } from '../lib/form-validation.js';
import { queryKeys } from '../lib/query-keys.js';
import { AccessEditor } from './AccessEditor.js';
import { Field } from './Field.js';

interface FormState {
  email: string;
  firstName: string;
  lastName: string;
  isAdmin: boolean;
  isActive: boolean;
  profileId: string | null;
  access: AccessSettings;
}

/** Add or edit a user: profile (ruolo-modello) and personal exceptions (docs/adr/ADR-008). */
export function UserDialog({ user, onClose }: { user: UserResponse | null; onClose: () => void }) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const queryClient = useQueryClient();
  const currentUser = useCurrentUser();
  const { config } = useAuth();
  const isSelf = user?.id === currentUser.id;
  const profiles = useQuery({
    queryKey: queryKeys.profiles,
    queryFn: () => apiRequest<ProfileResponse[]>('/profiles'),
  });

  const [form, setForm] = useState<FormState>(() => ({
    email: user?.email ?? '',
    firstName: user?.firstName ?? '',
    lastName: user?.lastName ?? '',
    isAdmin: user?.isAdmin ?? false,
    isActive: user?.isActive ?? true,
    profileId: user?.profileId ?? null,
    access: user?.access ?? NO_ACCESS,
  }));
  const [errors, setErrors] = useState<FieldErrors>({});
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    dialogRef.current?.showModal();
  }, []);

  const save = useMutation({
    mutationFn: (body: unknown) =>
      user
        ? apiRequest<UserResponse>(`/users/${user.id}`, { method: 'PATCH', body })
        : apiRequest<UserResponse>('/users', { method: 'POST', body }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.users });
      await queryClient.invalidateQueries({ queryKey: queryKeys.profiles });
      onClose();
    },
    onError: (error) => showServerError(error, setErrors, setFormError),
  });

  const submit = (event: FormEvent) => {
    event.preventDefault();
    setFormError(null);
    const body = {
      email: form.email,
      firstName: form.firstName,
      lastName: form.lastName,
      isAdmin: form.isAdmin,
      profileId: form.profileId,
      access: form.access,
      ...(user && { isActive: form.isActive }),
    };
    const result = user ? validate(updateUserSchema, body) : validate(createUserSchema, body);
    setErrors(result.errors);
    // The raw body is sent: the API normalises the access the same way.
    if (Object.keys(result.errors).length === 0) save.mutate(body);
  };

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((current) => ({ ...current, [key]: value }));

  const internalDomains = config?.mode === 'firebase' ? config.internalEmailDomains : ['k-rev.com'];
  const signInMethod = form.email.includes('@')
    ? expectedSignInMethod(form.email.trim().toLowerCase(), internalDomains)
    : null;
  const profile = profiles.data?.find((item) => item.id === form.profileId) ?? null;

  return (
    <dialog ref={dialogRef} className="dialog wide-dialog" onClose={onClose}>
      <form onSubmit={submit} className="stack" noValidate>
        <h2>{user ? `${user.lastName} ${user.firstName}` : 'Nuovo utente'}</h2>

        <div className="form-grid">
          <Field label="Nome" error={errors.firstName}>
            <input value={form.firstName} onChange={(e) => set('firstName', e.target.value)} />
          </Field>
          <Field label="Cognome" error={errors.lastName}>
            <input value={form.lastName} onChange={(e) => set('lastName', e.target.value)} />
          </Field>
          <Field
            label="E-mail"
            error={errors.email}
            hint={
              signInMethod === 'microsoft'
                ? 'Accederà con Microsoft 365'
                : signInMethod === 'password'
                  ? 'Accederà con e-mail e password: al primo accesso sceglie la password'
                  : undefined
            }
          >
            <input type="email" value={form.email} onChange={(e) => set('email', e.target.value)} />
          </Field>
        </div>

        <label className="checkbox">
          <input
            type="checkbox"
            checked={form.isAdmin}
            disabled={isSelf}
            onChange={(e) => set('isAdmin', e.target.checked)}
          />
          Amministratore: accesso completo a tutti i moduli e a tutti i dati, gestione di utenti,
          profili di accesso, backup e registro
        </label>
        {user && (
          <label className="checkbox">
            <input
              type="checkbox"
              checked={form.isActive}
              disabled={isSelf}
              onChange={(e) => set('isActive', e.target.checked)}
            />
            Attivo: può accedere a K-Management
          </label>
        )}

        {!form.isAdmin && (
          <>
            <Field
              label="Profilo di accesso"
              error={errors.profileId}
              hint={
                profile
                  ? `${profile.description ?? 'Livelli e funzioni del profilo'}. Le modifiche al profilo valgono per tutti i suoi utenti.`
                  : 'Senza profilo gli accessi si impostano solo qui sotto.'
              }
            >
              <select
                value={form.profileId ?? ''}
                onChange={(e) => set('profileId', e.target.value || null)}
              >
                <option value="">Nessun profilo: accessi personalizzati</option>
                {profiles.data?.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.name}
                  </option>
                ))}
              </select>
            </Field>

            <fieldset className="stack">
              <legend>{profile ? 'Eccezioni per questo utente' : 'Accesso ai moduli'}</legend>
              {profile && (
                <p className="muted small">
                  Lascia «Come il profilo» dove vale il profilo; ciò che imposti qui prevale solo
                  per {form.firstName || 'questo utente'}.
                </p>
              )}
              <AccessEditor
                mode="exceptions"
                value={form.access}
                base={profile ? profile.access : null}
                onChange={(access) => set('access', access)}
              />
            </fieldset>
          </>
        )}

        {formError && <p className="error">{formError}</p>}

        <div className="actions">
          <button type="button" onClick={onClose}>
            Annulla
          </button>
          <button type="submit" className="primary" disabled={save.isPending}>
            {save.isPending ? 'Salvataggio…' : 'Salva'}
          </button>
        </div>
      </form>
    </dialog>
  );
}
